import {
  DRAGONFLY_FULL,
  DRAGONFLY_REDUCED,
} from "$assets/brand/dragonfly-geometry";
import {
  EPOCUS_FONT_FAMILY,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_TEXT_PRIMARY,
} from "$routes/epocus/_lib/epocus-palette";
import React from "react";

const DEFAULT_SIZE = 40;
const BOX = 100;
const PULSE_STAGGER_SECONDS = 0.55;

const distanceFromThorax = (cx: number, cy: number): number =>
  Math.hypot(cx - BOX / 2, cy - BOX / 2) / (BOX / 2);

/**
 * This component must remain devoid of any dependencies of our contexts, as it is used in locations
 * outside of many of the core contexts.
 */
export const EpocusLogomark: FC<{
  size?: number;
  pulsing?: boolean;
  variant?: "full" | "reduced";
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({
  size = DEFAULT_SIZE,
  pulsing = false,
  variant = "full",
  color = EPOCUS_ICON_ACTIVE,
  className,
  style,
}) => {
  const dots = variant === "reduced" ? DRAGONFLY_REDUCED : DRAGONFLY_FULL;

  return (
    <svg
      className={className}
      style={style}
      width={size}
      height={size}
      viewBox={`0 0 ${BOX} ${BOX}`}
      fill="none"
      role="img"
      aria-label="ePOCUS"
    >
      {dots.map(([cx, cy, r], index) => (
        <circle key={`${cx}-${cy}-${index}`} cx={cx} cy={cy} r={r} fill={color}>
          {pulsing && (
            <animate
              attributeName="opacity"
              values="1;0.45;1"
              dur="2.2s"
              begin={`${(
                distanceFromThorax(cx, cy) * PULSE_STAGGER_SECONDS
              ).toFixed(2)}s`}
              repeatCount="indefinite"
            />
          )}
        </circle>
      ))}
    </svg>
  );
};

export const EpocusWordmark: FC<{ size?: number }> = ({ size = 21 }) => (
  <span
    style={{
      fontFamily: EPOCUS_FONT_FAMILY,
      fontSize: size,
      fontWeight: 700,
      lineHeight: 1.2,
      color: EPOCUS_TEXT_PRIMARY,
    }}
  >
    ePOCUS
  </span>
);

export const EpocusBrandLockup: FC<{
  markSize?: number;
  wordmarkSize?: number;
}> = ({ markSize = 120, wordmarkSize = 28 }) => (
  <span
    style={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
    }}
  >
    <EpocusLogomark size={markSize} pulsing />
    <EpocusWordmark size={wordmarkSize} />
  </span>
);
